import { ArrowLeft, Pencil, Plus, Download, X, ChevronRight, Paperclip, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { downloadFileFromUrl } from "../../../lib/utils";
import {
  addDocumentToConsultation,
  ALLOWED_MIME_TYPES,
  closeConsultationPro,
  createConsultationPro,
  updateConsultationPro,
  createVaccinationPro,
  getConsultationPdfPro,
  getOrdonnancePdfPro,
  getPatientByNip,
  getPatientConsultationsPro,
  getPatientMedicalFilePro,
  getPatientPrescriptionsPro,
  getPatientVaccinationsPro,
  updateDossierMedicalInfoPro,
  type TypeDocument,
} from "../../../api/professionalPatient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  BLOOD_GROUP_OPTIONS,
  ELECTROPHORESE_OPTIONS,
  UNSET_OPTION,
  withCurrentValue,
  emptyAntecedent,
  dateInputValue,
  extractRawMedFile,
  type RawAntecedent,
  type RawMedFile,
} from "../../../lib/dossierMedicalEdit";
import PharmacyOrdonnanceInline from "../../../features/pro/PharmacyOrdonnanceInline";
import { filterOrdonnanceRowsForConsultation } from "../../../features/pro/ordonnancePharmacyHelpers";
import { buildOrdonnanceSendOptionsFromProRows } from "../../../features/pro/SendOrdonnanceToPharmacyDialog";
import { getMaladiesEpidemiques, getPublicPharmacies, type Pharmacie } from "../../../api/publicBenin";
import { getMesEtablissements, sendOrdonnanceToPharmacie, type MonEtablissementItem } from "../../../api/professional";
import { uploadFilesToBucket } from "../../../api/bucket";
import { showToast } from "../../../components/ui/toastHotSonner";
import FileAttachmentLink, { type FileAttachmentData } from "../../../components/ui/FileAttachment";
import PatientAccessOtpModal, { type ContactSureteDisponible } from "../../../components/PatientAccessOtpModal";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import {
  CONSULTATION_EXIT_STATES,
  CONSULTATION_STEPS,
  buildConsultationComplement,
  createEmptyNewConsultationForm,
  truncateObservations,
  extractConsultationEntityId,
  type ConsultationExitState,
  type NewConsultationFormState,
} from "../../../features/pro/consultationComplement";

function ageFromIso(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const years = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return `${years} ans`;
}

function formatBirthFr(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
}

function mapConsultationToHistory(c: unknown, index: number): HistoryEntry {
  const o = c && typeof c === "object" ? (c as Record<string, unknown>) : {};
  const rawDate = o.date ?? o.createdAt ?? o.updatedAt;
  const parsed = typeof rawDate === "string" ? new Date(rawDate) : null;
  const label =
    parsed && !Number.isNaN(parsed.getTime())
      ? parsed.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
      : "Consultation";
  const idRaw = o.id ?? o._id;
  const selectionKey =
    typeof idRaw === "string" && idRaw.trim()
      ? idRaw.trim()
      : typeof idRaw === "number" && Number.isFinite(idRaw)
        ? String(idRaw)
        : typeof rawDate === "string" && rawDate
          ? rawDate
          : `idx-${index}`;

  let analyses: string[] | undefined;
  let resultats: { disease: string; note: string } | undefined;
  let ordonnances: { medicament: string; note: string; renouvelable: boolean }[] | undefined;

  // Contrat backend confirmé (QUESTION_CONTRAT_HISTORIQUE_CONSULTATIONS.md) : `complement` est
  // toujours un objet ConsultationComplement déjà parsé, ou `null` — jamais une string à parser,
  // et analyses/resultats/ordonnances ne vivent que là, jamais dupliqués au niveau racine.
  const complement =
    o.complement && typeof o.complement === "object" && !Array.isArray(o.complement)
      ? (o.complement as Record<string, unknown>)
      : null;
  if (complement) {
    if (Array.isArray(complement.analyses)) {
      analyses = complement.analyses.map((a) => (typeof a === "string" ? a : String(a)));
    }
    if (
      complement.resultats &&
      typeof complement.resultats === "object" &&
      !Array.isArray(complement.resultats)
    ) {
      const r = complement.resultats as Record<string, unknown>;
      resultats = {
        disease: String(r.disease ?? "—"),
        note: String(r.note ?? ""),
      };
    }
    if (Array.isArray(complement.ordonnances)) {
      ordonnances = (complement.ordonnances as Record<string, unknown>[]).map((row) => ({
        medicament: String(row.medicament ?? ""),
        note: String(row.note ?? ""),
        renouvelable: row.renouvelable === true,
      }));
    }
  }

  let documents: FileAttachmentData[] | undefined;
  if (Array.isArray(o.documents)) {
    documents = (o.documents as Record<string, unknown>[])
      .map((d) => ({
        url: String(d.bucketUrl ?? d.url ?? ""),
        fileName: String(d.fileName ?? d.name ?? "Document"),
        mimeType: typeof d.mimeType === "string" ? d.mimeType : undefined,
      }))
      .filter((d) => d.url);
  }

  return {
    id:
      typeof idRaw === "string"
        ? idRaw
        : typeof idRaw === "number" && Number.isFinite(idRaw)
          ? String(idRaw)
          : undefined,
    selectionKey,
    label,
    doctor: [o.professionnelPrenom, o.professionnelNom]
      .filter((v) => typeof v === "string" && (v as string).trim())
      .join(" ") || String(o.professionnelNip ?? o.professionnel_nip ?? "—"),
    professionnelNip:
      typeof o.professionnelNip === "string"
        ? o.professionnelNip
        : typeof o.professionnel_nip === "string"
          ? o.professionnel_nip
          : undefined,
    statutConsultation:
      typeof o.statutConsultation === "string"
        ? o.statutConsultation
        : typeof o.statut === "string"
          ? o.statut
          : undefined,
    etatSortie: typeof o.etatSortie === "string" ? o.etatSortie : undefined,
    dateCloture:
      typeof o.dateCloture === "string"
        ? o.dateCloture
        : typeof o.closedAt === "string"
          ? o.closedAt
          : undefined,
    motif: typeof o.motif === "string" ? o.motif : undefined,
    observations: typeof o.observations === "string" ? o.observations : undefined,
    analyses,
    resultats,
    ordonnances,
    documents,
  };
}

type HistoryEntry = {
  id?: string;
  /** Clé stable pour la sélection (id consultation ou date ISO / index). */
  selectionKey: string;
  label: string;
  doctor: string;
  professionnelNip?: string;
  statutConsultation?: string;
  etatSortie?: string;
  dateCloture?: string;
  motif?: string;
  observations?: string;
  analyses?: string[];
  resultats?: { disease: string; note: string };
  ordonnances?: { medicament: string; note: string; renouvelable: boolean }[];
  documents?: FileAttachmentData[];
};

export default function ProPatientDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { professional } = useProfessionalAuth();
  const proNip = professional?.nip?.trim();

  const [displayPatient, setDisplayPatient] = useState({
    nip: "",
    nom: "",
    prenom: "",
    email: "",
    phone: "",
    birth: "",
    job: "",
    age: "",
    height: "",
    address: "",
    blood: "",
    antecedents: "",
    createur: null as string | null,
  });
  const [medRaw, setMedRaw] = useState<RawMedFile | null>(null);
  const [editingMed, setEditingMed] = useState(false);
  const [medForm, setMedForm] = useState<RawMedFile | null>(null);
  const [newAllergie, setNewAllergie] = useState("");
  const [newIntolerance, setNewIntolerance] = useState("");
  const [savingMed, setSavingMed] = useState(false);
  const [medEditError, setMedEditError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [needsAccessOtp, setNeedsAccessOtp] = useState(false);
  const [accessRequiredPatientEstMineur, setAccessRequiredPatientEstMineur] = useState(false);
  const [accessRequiredContacts, setAccessRequiredContacts] = useState<ContactSureteDisponible[]>([]);
  const [accessUrgenceDisponible, setAccessUrgenceDisponible] = useState(false);
  const [monEtablissements, setMonEtablissements] = useState<MonEtablissementItem[]>([]);
  const [monEtablissementsLoading, setMonEtablissementsLoading] = useState(false);
  const [selectedEtablissementId, setSelectedEtablissementId] = useState("");

  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [view, setView] = useState<'overview' | 'history'>('overview');
  const [selectedHistoryKey, setSelectedHistoryKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'observations' | 'analyses' | 'resultats' | 'ordonnances'>('observations');
  const [disease, setDisease] = useState('Maladie');
  const [resultNote, setResultNote] = useState('');
  const [maladiesReferentiel, setMaladiesReferentiel] = useState<string[]>([]);
  const [loadingMaladies, setLoadingMaladies] = useState(true);
  const [diseaseAutreMode, setDiseaseAutreMode] = useState(false);
  const [newConsultationDiseaseAutreMode, setNewConsultationDiseaseAutreMode] = useState(false);
  type PendingDoc = { bucketUrl: string; fileName: string; fileSize: number; mimeType: string; type: TypeDocument };
  const [analysisFiles, setAnalysisFiles] = useState<File[]>([]);
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [sentDocs, setSentDocs] = useState<PendingDoc[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** Input fichier dédié à la modale "Nouvelle consultation" — la vue onglets (derrière la
   * modale) reste montée pendant que la modale est ouverte, donc deux refs distinctes évitent
   * que l'un des deux `<input type="file">` écrase la ref de l'autre. */
  const modalFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadFileError, setUploadFileError] = useState<string | null>(null);
  const [prescriptions, setPrescriptions] = useState<Array<{ medicament: string; note: string; renouvelable: boolean }>>([
    { medicament: 'medicaments', note: 'note...', renouvelable: false },
  ]);

  type VaccinationEntry = { id: string; nom: string; date: string };
  const [vaccinations, setVaccinations] = useState<VaccinationEntry[]>([]);
  const [showAddVaccination, setShowAddVaccination] = useState(false);
  const [newVaccination, setNewVaccination] = useState({ nom: '', date: '', statut: 'Effectué' });

  const [showAddConsultation, setShowAddConsultation] = useState(false);
  const [consultationStep, setConsultationStep] = useState(0);
  const [newConsultation, setNewConsultation] = useState<NewConsultationFormState>(() =>
    createEmptyNewConsultationForm(),
  );
  const [etatSortie, setEtatSortie] = useState<ConsultationExitState>("en_traitement");
  const [draftConsultationId, setDraftConsultationId] = useState<string | null>(null);
  const [draftConsultationStatus, setDraftConsultationStatus] = useState<"ouverte" | "fermee" | "annulee" | null>(null);
  const [savingConsultation, setSavingConsultation] = useState(false);
  const [closingHistoryConsultation, setClosingHistoryConsultation] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [historyPharmacyOrdoOptions, setHistoryPharmacyOrdoOptions] = useState<{ id: string; label: string }[]>([]);
  const [historyPharmacyOrdoLoading, setHistoryPharmacyOrdoLoading] = useState(false);
  const [historyPharmacyOrdoError, setHistoryPharmacyOrdoError] = useState<string | null>(null);
  const [pendingPharmacyConsultationId, setPendingPharmacyConsultationId] = useState<string | null>(null);
  const [consultationPdfLoading, setConsultationPdfLoading] = useState(false);
  const [ordonnancePdfLoading, setOrdonnancePdfLoading] = useState(false);

  /** Modal étape 2 : liste pharmacies + envoi (après clôture, IDs ordonnance côté API). */
  const [modalConsultPharmacies, setModalConsultPharmacies] = useState<Pharmacie[]>([]);
  const [modalConsultPharmaciesLoading, setModalConsultPharmaciesLoading] = useState(false);
  const [modalConsultSelectedPharmacieId, setModalConsultSelectedPharmacieId] = useState("");
  const [modalPostCloseOrdoOptions, setModalPostCloseOrdoOptions] = useState<{ id: string; label: string }[]>([]);
  const [modalConsultSelectedOrdonnanceId, setModalConsultSelectedOrdonnanceId] = useState("");
  const [modalConsultSending, setModalConsultSending] = useState(false);
  const [modalOrdoRefetching, setModalOrdoRefetching] = useState(false);
  const [modalConsultSendError, setModalConsultSendError] = useState<string | null>(null);

  const resetConsultationForm = () => {
    setConsultationStep(0);
    setNewConsultation(createEmptyNewConsultationForm());
    setEtatSortie("en_traitement");
    setDraftConsultationId(null);
    setDraftConsultationStatus(null);
    setModalConsultPharmacies([]);
    setModalConsultPharmaciesLoading(false);
    setModalConsultSelectedPharmacieId("");
    setModalPostCloseOrdoOptions([]);
    setModalConsultSelectedOrdonnanceId("");
    setModalConsultSending(false);
    setModalOrdoRefetching(false);
    setModalConsultSendError(null);
    setAnalysisFiles([]);
    setPendingDocs([]);
    setSentDocs([]);
    setUploadFileError(null);
  };

  const reloadPatientData = useCallback(async () => {
    if (!id) return;
    const nip = decodeURIComponent(id);
    /** Évite GET …/patient/…/0 (400) si la liste a utilisé un index ou un id métier à la place du NIP. */
    if (!/^\d{10}$/.test(nip)) {
      setDetailLoading(false);
      setDetailError(
        "NIP patient invalide (10 chiffres requis). Revenez à la liste et sélectionnez une ligne dont le NUM ANIP est renseigné.",
      );
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    const [pRes, dmRes, cRes, vRes] = await Promise.all([
      getPatientByNip(nip),
      getPatientMedicalFilePro(nip),
      getPatientConsultationsPro(nip),
      getPatientVaccinationsPro(nip),
    ]);
    if (!pRes.ok) {
      setDetailError(pRes.message);
      setDetailLoading(false);
      return;
    }
    const accessBlocker = [dmRes, cRes, vRes].find(
      (r) => !r.ok && (r as { errorCode?: string }).errorCode === "ACCES_DOSSIER_REQUIS"
    ) as { meta?: Record<string, unknown> } | undefined;
    setNeedsAccessOtp(!!accessBlocker);
    setAccessRequiredPatientEstMineur(accessBlocker?.meta?.patientEstMineur === true);
    setAccessRequiredContacts(
      Array.isArray(accessBlocker?.meta?.contactsSureteDisponibles) ? accessBlocker.meta.contactsSureteDisponibles : []
    );
    setAccessUrgenceDisponible(accessBlocker?.meta?.accesUrgenceDisponible === true);
    const pr = pRes.data as Record<string, unknown>;
    const dm =
      dmRes.ok && dmRes.data && typeof dmRes.data === "object"
        ? (dmRes.data as Record<string, unknown>)
        : null;
    const ip = (dm?.informationsPersonnelles as Record<string, unknown> | undefined) ?? {};
    const im = (dm?.informationsMedicales as Record<string, unknown> | undefined) ?? {};
    const dn =
      typeof pr.dateNaissance === "string"
        ? pr.dateNaissance
        : typeof pr.date_naissance === "string"
          ? pr.date_naissance
          : typeof ip.dateNaissance === "string"
            ? ip.dateNaissance
            : "";
    setDisplayPatient({
      nip: String(pr.nip ?? nip),
      nom: String(pr.nom ?? ip.nom ?? ""),
      prenom: String(pr.prenom ?? ip.prenom ?? ""),
      email: String(pr.email ?? ip.email ?? ""),
      phone: String(pr.telephone ?? ip.telephone ?? ""),
      birth: formatBirthFr(dn),
      job: String(pr.profession ?? ip.profession ?? ""),
      age: ageFromIso(dn),
      height:
        im.taille != null
          ? `${im.taille} cm`
          : pr.taille != null
            ? `${pr.taille} cm`
            : "—",
      address: String(pr.adresse ?? ip.adresse ?? ""),
      blood: String(pr.groupeSanguin ?? im.groupeSanguin ?? "—"),
      antecedents: Array.isArray(im.antecedents)
        ? (im.antecedents as Array<{ type?: string; description?: string }>)
            .map((a) => {
              const desc = (a.description ?? "").trim();
              const type = (a.type ?? "").trim();
              if (desc && type) return `${type} — ${desc}`;
              return desc || type;
            })
            .filter(Boolean)
            .join("; ")
        : "",
      createur: typeof pr.createur === "string" ? pr.createur : null,
    });
    setMedRaw(dm && dm.existe !== false ? extractRawMedFile(dm) : null);
    setEditingMed(false);
    setSelectedEtablissementId("");
    const consultations = cRes.ok ? cRes.data : [];
    setHistoryEntries(consultations.map((row, i) => mapConsultationToHistory(row, i)));
    const vac = vRes.ok ? vRes.data : [];
    setVaccinations(
      vac.map((item, idx) => {
        const o = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
        const nomV = String(o.vaccin ?? o.libelleVaccin ?? o.typeVaccin ?? "Vaccin");
        const rawDate = String(o.dateVaccination ?? o.date ?? "");
        const parsed = rawDate ? new Date(rawDate) : null;
        const dateV = parsed && !Number.isNaN(parsed.getTime())
          ? parsed.toLocaleDateString("fr-FR")
          : rawDate || "—";
        return { id: String(o.id ?? idx), nom: nomV, date: dateV };
      })
    );
    setDetailLoading(false);
  }, [id, proNip]);

  useEffect(() => {
    void reloadPatientData();
  }, [reloadPatientData]);

  const startEditMed = () => {
    if (!medRaw) return;
    setMedForm(medRaw);
    setNewAllergie("");
    setNewIntolerance("");
    setMedEditError(null);
    setEditingMed(true);
  };

  const cancelEditMed = () => {
    setEditingMed(false);
    setMedEditError(null);
  };

  const updateMedField = <K extends keyof RawMedFile>(key: K, value: RawMedFile[K]) => {
    setMedForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateAntecedent = (index: number, patch: Partial<RawAntecedent>) => {
    setMedForm((prev) => {
      if (!prev) return prev;
      const antecedents = prev.antecedents.map((a, i) => (i === index ? { ...a, ...patch } : a));
      return { ...prev, antecedents };
    });
  };

  const removeAntecedent = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, antecedents: prev.antecedents.filter((_, i) => i !== index) } : prev));
  };

  const addAllergie = () => {
    const value = newAllergie.trim();
    if (!value) return;
    setMedForm((prev) => (prev ? { ...prev, allergies: [...prev.allergies, value] } : prev));
    setNewAllergie("");
  };

  const removeAllergie = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, allergies: prev.allergies.filter((_, i) => i !== index) } : prev));
  };

  const addIntolerance = () => {
    const value = newIntolerance.trim();
    if (!value) return;
    setMedForm((prev) => (prev ? { ...prev, intolerances: [...prev.intolerances, value] } : prev));
    setNewIntolerance("");
  };

  const removeIntolerance = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, intolerances: prev.intolerances.filter((_, i) => i !== index) } : prev));
  };

  const handleSaveMed = async () => {
    if (!displayPatient.nip || !medForm) return;
    setSavingMed(true);
    setMedEditError(null);
    const res = await updateDossierMedicalInfoPro(displayPatient.nip, medForm);
    setSavingMed(false);
    if (!res.ok) {
      setMedEditError(res.message || "Impossible d'enregistrer les modifications.");
      return;
    }
    showToast("Dossier médical mis à jour", "success");
    setEditingMed(false);
    void reloadPatientData();
  };

  /** Liste des pharmacies (dropdown) dans le modal — étape clôture. */
  useEffect(() => {
    if (!showAddConsultation || consultationStep !== 1) return;
    setModalConsultPharmaciesLoading(true);
    void getPublicPharmacies({ limit: 100 })
      .then((res) => {
        if (!res.ok) {
          setModalConsultPharmacies([]);
          return;
        }
        setModalConsultPharmacies(res.data);
      })
      .finally(() => setModalConsultPharmaciesLoading(false));
  }, [showAddConsultation, consultationStep]);

  /** Établissements (et cabinets) où ce professionnel a une liaison active — sélecteur consultation rapide. */
  useEffect(() => {
    if (!proNip) return;
    setMonEtablissementsLoading(true);
    void getMesEtablissements(proNip)
      .then((res) => {
        if (res.ok) setMonEtablissements(res.data);
      })
      .finally(() => setMonEtablissementsLoading(false));
  }, [proNip]);

  useEffect(() => {
    setLoadingMaladies(true);
    void getMaladiesEpidemiques()
      .then((res) => {
        if (res.ok) setMaladiesReferentiel(res.data);
      })
      .finally(() => setLoadingMaladies(false));
  }, []);

  /**
   * Bascule le champ "Diagnostic / Résultats" (édition d'une consultation existante) en mode
   * "Autre" dès que sa valeur chargée ne correspond à aucune maladie du référentiel — cas des
   * diagnostics en texte libre saisis avant ce correctif.
   */
  useEffect(() => {
    if (loadingMaladies || maladiesReferentiel.length === 0) return;
    if (disease === "Maladie" || disease === "" || disease === "autre") return;
    if (!maladiesReferentiel.includes(disease)) setDiseaseAutreMode(true);
  }, [disease, maladiesReferentiel, loadingMaladies]);

  /** Après clôture depuis le modal : bascule vers Historique > Ordonnances pour l’envoi à la pharmacie. */
  useEffect(() => {
    if (!pendingPharmacyConsultationId) return;
    const ent = historyEntries.find((e) => e.id === pendingPharmacyConsultationId);
    if (ent) {
      setView("history");
      setSelectedHistoryKey(ent.selectionKey);
      setActiveTab("ordonnances");
      setPendingPharmacyConsultationId(null);
    }
  }, [pendingPharmacyConsultationId, historyEntries]);

  /** Charge les identifiants d’ordonnance (API) pour la consultation affichée dans l’onglet Ordonnances. */
  useEffect(() => {
    if (!id || view !== "history" || activeTab !== "ordonnances" || selectedHistoryKey == null) {
      setHistoryPharmacyOrdoOptions([]);
      setHistoryPharmacyOrdoLoading(false);
      setHistoryPharmacyOrdoError(null);
      return;
    }
    const entry = historyEntries.find((e) => e.selectionKey === selectedHistoryKey);
    const statut = (entry?.statutConsultation ?? "").toLowerCase();
    if (!entry?.id || statut === "ouverte" || !entry.ordonnances?.length) {
      setHistoryPharmacyOrdoOptions([]);
      setHistoryPharmacyOrdoLoading(false);
      setHistoryPharmacyOrdoError(null);
      return;
    }
    let cancelled = false;
    const consultationId = entry.id;
    void (async () => {
      setHistoryPharmacyOrdoLoading(true);
      setHistoryPharmacyOrdoError(null);
      const patientNip = decodeURIComponent(id);
      const loadOnce = async (): Promise<
        | { ok: true; data: { id: string; label: string }[] }
        | { ok: false; message: string }
      > => {
        const res = await getPatientPrescriptionsPro(patientNip);
        if (!res.ok) return { ok: false, message: res.message };
        const matched = filterOrdonnanceRowsForConsultation(res.data, consultationId);
        return { ok: true, data: buildOrdonnanceSendOptionsFromProRows(matched) };
      };
      let result = await loadOnce();
      const retryDelays = [500, 1000, 2000];
      for (const delay of retryDelays) {
        if (cancelled) break;
        if (result.ok && result.data.length > 0) break;
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled) break;
        result = await loadOnce();
      }
      if (cancelled) return;
      if (!result.ok) {
        setHistoryPharmacyOrdoError(result.message);
        setHistoryPharmacyOrdoOptions([]);
        setHistoryPharmacyOrdoLoading(false);
        return;
      }
      setHistoryPharmacyOrdoOptions(result.data);
      setHistoryPharmacyOrdoError(null);
      setHistoryPharmacyOrdoLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, view, activeTab, selectedHistoryKey, historyEntries]);

  const handleAddVaccination = async () => {
    const nom = newVaccination.nom.trim();
    const date = newVaccination.date || new Date().toISOString().slice(0, 10);
    if (!nom || !id) return;
    const patientNip = decodeURIComponent(id);
    setActionError(null);
    if (!proNip) {
      setActionError("NIP professionnel manquant.");
      return;
    }
    const res = await createVaccinationPro({
      patientNip,
      vaccin: nom,
      dateVaccination: new Date(date).toISOString(),
      professionnelNip: proNip,
    });
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    setNewVaccination({ nom: '', date: '', statut: 'Effectué' });
    setShowAddVaccination(false);
    await reloadPatientData();
  };

  const handleUploadAnalysisFiles = async () => {
    if (analysisFiles.length === 0) return;

    const invalidFiles = analysisFiles.filter(
      (f) => !(ALLOWED_MIME_TYPES as readonly string[]).includes(f.type)
    );
    if (invalidFiles.length > 0) {
      setUploadFileError(
        `Type non autorisé : ${invalidFiles.map((f) => f.name).join(", ")}. Formats acceptés : PDF, images (PNG/JPG), Word, Excel.`
      );
      return;
    }

    setUploadingFiles(true);
    setUploadFileError(null);
    const folder = draftConsultationId
      ? `consultations-pieces-jointes-${draftConsultationId}`
      : "consultations-pieces-jointes";
    const res = await uploadFilesToBucket(analysisFiles, folder);
    setUploadingFiles(false);
    if (!res.ok) { setUploadFileError(res.message); return; }

    const newDocs: PendingDoc[] = analysisFiles.map((f, i) => ({
      bucketUrl: res.urls[i],
      fileName: f.name,
      fileSize: f.size,
      mimeType: f.type,
      type: "analyse" as TypeDocument,
    }));

    if (draftConsultationId) {
      // Consultation déjà ouverte → POST immédiat au backend
      const sent: PendingDoc[] = [];
      for (const doc of newDocs) {
        const r = await addDocumentToConsultation(draftConsultationId, doc);
        if (r.ok) sent.push(doc);
      }
      setSentDocs((prev) => [...prev, ...sent]);
      if (sent.length < newDocs.length) {
        setUploadFileError(`${newDocs.length - sent.length} fichier(s) non enregistré(s) côté backend.`);
      }
    } else {
      // Pas encore de consultation → on garde les docs en attente
      setPendingDocs((prev) => [...prev, ...newDocs]);
    }

    setAnalysisFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Persiste Observations/Analyses pour une consultation déjà ouverte qu'on est en train de
   * compléter (étape 1 du modal) — sans ce PUT, "Enregistrer"/"Suivant" ne faisaient qu'avancer
   * l'étape locale du wizard sans jamais rien envoyer au serveur (voir openCompleteConsultationModal).
   * Retourne `true` si l'enregistrement a réussi (ou n'était pas nécessaire).
   */
  const saveOpenConsultationObservations = async (): Promise<boolean> => {
    if (!draftConsultationId) return true;
    setSavingConsultation(true);
    setActionError(null);
    const obs = truncateObservations(newConsultation.observations);
    const res = await updateConsultationPro(draftConsultationId, {
      observations: obs || undefined,
      complement: { analyses: newConsultation.analyses },
    });
    setSavingConsultation(false);
    if (!res.ok) {
      setActionError(res.message);
      return false;
    }
    await reloadPatientData();
    return true;
  };

  const handleSubmitConsultation = async (saveOnly = false) => {
    if (!id || !proNip) {
      setActionError(!proNip ? "NIP professionnel manquant." : "Patient inconnu.");
      return;
    }
    if (draftConsultationStatus === "fermee") {
      setActionError("Cette consultation est déjà clôturée (lecture seule).");
      return;
    }
    const isOpening = !draftConsultationId;
    if (isOpening && !selectedEtablissementId) {
      setActionError("Sélectionnez l'établissement (ou votre cabinet) sous lequel créer cette consultation.");
      return;
    }
    const patientNip = decodeURIComponent(id);
    setActionError(null);
    setSavingConsultation(true);
    const complement = buildConsultationComplement(newConsultation) as Record<string, unknown> | undefined;
    const obs = truncateObservations(newConsultation.observations);
    const openComplement =
      complement && Array.isArray(complement.analyses) && complement.analyses.length
        ? { analyses: complement.analyses }
        : undefined;
    const closeComplement: Record<string, unknown> = {};
    if (complement?.resultats && typeof complement.resultats === "object") {
      closeComplement.resultats = complement.resultats;
    }
    if (Array.isArray(complement?.ordonnances) && complement.ordonnances.length > 0) {
      closeComplement.ordonnances = complement.ordonnances;
    }
    const promptPharmacyAfterModalClose =
      !isOpening &&
      !!draftConsultationId &&
      Array.isArray(complement?.ordonnances) &&
      complement.ordonnances.length > 0;
    const consultationIdForPharmacy = draftConsultationId;
    const res = isOpening
      ? await createConsultationPro({
          patientNip,
          professionnelNip: proNip,
          etablissementId: selectedEtablissementId,
          date: new Date().toISOString(),
          typeConsultation: "consultation_generale",
          motif: "Consultation initiale",
          observations: obs || undefined,
          createur: proNip,
          ...(openComplement ? { complement: openComplement } : {}),
        })
      : await closeConsultationPro(draftConsultationId, {
          professionnelNip: proNip,
          etatSortie,
          complement: closeComplement,
        });
    setSavingConsultation(false);
    if (!res.ok) {
      if (res.errorCode === "ETABLISSEMENT_LIAISON_REQUISE") {
        setActionError("Vous devez avoir une liaison active avec cet établissement pour y créer une consultation.");
        return;
      }
      if (res.status === 409) {
        setActionError("Cette consultation est déjà fermée ou annulée.");
        return;
      }
      if (res.status === 400) {
        setActionError("Données invalides pour cette étape (vérifiez les champs requis).");
        return;
      }
      if (res.status === 403) {
        setActionError(
          res.errorCode === "NOT_CONSULTATION_AUTHOR"
            ? res.message
            : "Droits insuffisants pour cette action.",
        );
        return;
      }
      setActionError(res.message);
      return;
    }
    if (isOpening) {
      const newId = extractConsultationEntityId(res.data);
      if (!newId) {
        setActionError("Consultation ouverte, mais identifiant introuvable dans la réponse serveur.");
        return;
      }
      // POST les docs en attente maintenant qu'on a l'ID
      if (pendingDocs.length > 0) {
        const sent: PendingDoc[] = [];
        for (const doc of pendingDocs) {
          const r = await addDocumentToConsultation(newId, doc);
          if (r.ok) sent.push(doc);
        }
        setSentDocs((prev) => [...prev, ...sent]);
        setPendingDocs([]);
      }
      await reloadPatientData();
      if (saveOnly) {
        setShowAddConsultation(false);
        resetConsultationForm();
        return;
      }
      setDraftConsultationId(newId);
      setDraftConsultationStatus("ouverte");
      setConsultationStep(1);
      return;
    }
    setDraftConsultationStatus("fermee");
    await reloadPatientData();
    if (promptPharmacyAfterModalClose && consultationIdForPharmacy) {
      const loadOrdoOptsOnce = async () => {
        const res = await getPatientPrescriptionsPro(patientNip);
        if (!res.ok) return [] as { id: string; label: string }[];
        const matched = filterOrdonnanceRowsForConsultation(res.data, consultationIdForPharmacy);
        return buildOrdonnanceSendOptionsFromProRows(matched);
      };
      const retryDelays = [0, 500, 1000, 1500, 2500, 3500];
      let opts: { id: string; label: string }[] = [];
      for (const d of retryDelays) {
        if (d) await new Promise((r) => setTimeout(r, d));
        opts = await loadOrdoOptsOnce();
        if (opts.length > 0) break;
      }
      setModalPostCloseOrdoOptions(opts);
      setModalConsultSelectedOrdonnanceId(opts[0]?.id ?? "");
      setModalConsultSendError(null);
      return;
    }
    setShowAddConsultation(false);
    resetConsultationForm();
  };

  const handleModalSendOrdonnanceToPharmacy = async () => {
    const ordonnanceId = (modalConsultSelectedOrdonnanceId || modalPostCloseOrdoOptions[0]?.id || "").trim();
    const pharmacieId = modalConsultSelectedPharmacieId.trim();
    if (!ordonnanceId || !pharmacieId) {
      setModalConsultSendError(
        modalPostCloseOrdoOptions.length === 0
          ? "Aucune ordonnance exploitable (vérifiez la réponse réseau ou une consultation ancienne sans enregistrement SQL). Réessayez ou consultez l'historique."
          : "Sélectionnez une pharmacie.",
      );
      return;
    }
    setModalConsultSendError(null);
    setModalConsultSending(true);
    const res = await sendOrdonnanceToPharmacie(ordonnanceId, pharmacieId);
    setModalConsultSending(false);
    if (!res.ok) {
      setModalConsultSendError(res.message);
      return;
    }
    showToast("Ordonnance envoyée à la pharmacie.", "success");
  };

  const handleModalTerminerApresEnvoi = () => {
    const cid = draftConsultationId;
    setShowAddConsultation(false);
    resetConsultationForm();
    if (cid) setPendingPharmacyConsultationId(cid);
  };

  const handleRetryModalOrdoFetch = useCallback(async () => {
    if (!id || !draftConsultationId) return;
    const patientNip = decodeURIComponent(id);
    const cid = draftConsultationId;
    setModalConsultSendError(null);
    setModalOrdoRefetching(true);
    try {
      const loadOnce = async () => {
        const res = await getPatientPrescriptionsPro(patientNip);
        if (!res.ok) {
          setModalConsultSendError(res.message);
          return [] as { id: string; label: string }[];
        }
        const matched = filterOrdonnanceRowsForConsultation(res.data, cid);
        return buildOrdonnanceSendOptionsFromProRows(matched);
      };
      const retryDelays = [0, 500, 1000, 2000, 3000];
      let opts: { id: string; label: string }[] = [];
      for (const d of retryDelays) {
        if (d) await new Promise((r) => setTimeout(r, d));
        opts = await loadOnce();
        if (opts.length > 0) break;
      }
      setModalPostCloseOrdoOptions(opts);
      setModalConsultSelectedOrdonnanceId(opts[0]?.id ?? "");
    } finally {
      setModalOrdoRefetching(false);
    }
  }, [id, draftConsultationId]);

  const currentHistoryEntry = selectedHistoryKey
    ? historyEntries.find((e) => e.selectionKey === selectedHistoryKey)
    : null;
  const isCurrentConsultationOpen = (currentHistoryEntry?.statutConsultation ?? "").toLowerCase() === "ouverte";
  // Seul l'auteur peut modifier/clôturer une consultation tant qu'elle est ouverte (contrôle miroir du backend,
  // voir docs/frontend/patient/QUESTION_OWNERSHIP_MODIFICATION_CONSULTATION.md — ceci est une aide UX,
  // le contrôle qui compte réellement est côté serveur).
  const isCurrentConsultationAuthor = Boolean(proNip) && currentHistoryEntry?.professionnelNip === proNip;
  const canCloseCurrentConsultation = Boolean(
    isCurrentConsultationOpen && isCurrentConsultationAuthor && currentHistoryEntry?.id && proNip,
  );

  const selectedHistoryLabel =
    selectedHistoryKey != null
      ? historyEntries.find((e) => e.selectionKey === selectedHistoryKey)?.label
      : null;

  useEffect(() => {
    if (!currentHistoryEntry) return;
    setDisease(currentHistoryEntry.resultats?.disease || "Maladie");
    setDiseaseAutreMode(false);
    setResultNote(currentHistoryEntry.resultats?.note || "");
    setPrescriptions(
      currentHistoryEntry.ordonnances && currentHistoryEntry.ordonnances.length > 0
        ? currentHistoryEntry.ordonnances.map((row) => ({
            medicament: row.medicament ?? "",
            note: row.note ?? "",
            renouvelable: row.renouvelable === true,
          }))
        : [{ medicament: "", note: "", renouvelable: false }]
    );
  }, [currentHistoryEntry?.selectionKey]);

  /**
   * "Compléter" une consultation déjà ouverte : sautait directement à l'étape 2
   * (Résultats & Ordonnances) sans jamais montrer ni pré-remplir Observations/Analyses. Le
   * professionnel n'avait alors aucun moyen de les saisir ou de les compléter avant clôture —
   * elles restaient vides dans l'historique (point_correction.md DAY_8, bug #8). On repart
   * désormais de l'étape 1, pré-remplie avec les valeurs déjà enregistrées le cas échéant.
   */
  const openCompleteConsultationModal = () => {
    if (!currentHistoryEntry) return;
    resetConsultationForm();
    setNewConsultation((p) => ({
      ...p,
      observations: currentHistoryEntry.observations ?? "",
      analyses: currentHistoryEntry.analyses ?? [],
    }));
    setDraftConsultationId(currentHistoryEntry.id ?? null);
    setDraftConsultationStatus("ouverte");
    setConsultationStep(0);
    setShowAddConsultation(true);
  };

  const handleCloseFromHistory = async (options?: { allowEmpty?: boolean }) => {
    if (!currentHistoryEntry?.id || !proNip) {
      setActionError("Impossible de clôturer cette consultation.");
      return;
    }
    if (!isCurrentConsultationOpen) {
      setActionError("Seules les consultations ouvertes peuvent être clôturées.");
      return;
    }
    const safeDisease = disease.trim();
    const safeNote = resultNote.trim();
    const ordonnances = prescriptions
      .filter((row) => row.medicament.trim())
      .map((row) => ({ medicament: row.medicament.trim(), note: row.note.trim(), renouvelable: row.renouvelable }));
    if (!options?.allowEmpty && !safeDisease && !safeNote && ordonnances.length === 0) {
      setActionError("Renseignez au moins un résultat ou une ordonnance avant la clôture.");
      return;
    }
    const complement: Record<string, unknown> = {};
    if (safeDisease || safeNote) {
      complement.resultats = { disease: safeDisease || "Non précisé", note: safeNote };
    }
    if (ordonnances.length > 0) {
      complement.ordonnances = ordonnances;
    }

    setActionError(null);
    setClosingHistoryConsultation(true);
    const consultationClosedId = currentHistoryEntry.id;
    const hadOrdonnancesInCloseBody = ordonnances.length > 0;
    const res = await closeConsultationPro(currentHistoryEntry.id, {
      professionnelNip: proNip,
      etatSortie: "en_traitement",
      complement,
    });
    setClosingHistoryConsultation(false);
    if (!res.ok) {
      if (res.status === 409) {
        setActionError("Cette consultation est déjà fermée ou annulée.");
        return;
      }
      if (res.status === 400) {
        setActionError("Données invalides pour la clôture (résultats/ordonnances).");
        return;
      }
      if (res.status === 403) {
        setActionError(
          res.errorCode === "NOT_CONSULTATION_AUTHOR"
            ? res.message
            : "Droits insuffisants pour clôturer cette consultation.",
        );
        return;
      }
      setActionError(res.message);
      return;
    }
    await reloadPatientData();
    if (hadOrdonnancesInCloseBody && consultationClosedId) {
      setActiveTab("ordonnances");
    }
  };

  const handleDownloadConsultationPdf = async () => {
    if (!currentHistoryEntry?.id) return;
    setConsultationPdfLoading(true);
    setActionError(null);
    const res = await getConsultationPdfPro(currentHistoryEntry.id);
    setConsultationPdfLoading(false);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `consultation-${currentHistoryEntry.id}.pdf`);
  };

  const historyOrdonnanceId = historyPharmacyOrdoOptions[0]?.id;

  const handleDownloadOrdonnancePdf = async () => {
    if (!historyOrdonnanceId) return;
    setOrdonnancePdfLoading(true);
    setActionError(null);
    const res = await getOrdonnancePdfPro(historyOrdonnanceId);
    setOrdonnancePdfLoading(false);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `ordonnance-${historyOrdonnanceId}.pdf`);
  };

  return (
    <div>
      {actionError && (
        <div className="mb-4 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {actionError}
        </div>
      )}
      {detailError && (
        <div className="mb-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          {detailError}
        </div>
      )}
      {detailLoading && (
        <div className="mb-4 text-white/90 text-sm">Chargement du dossier…</div>
      )}

      {/* En-tête */}
      <div className="text-white">
        <button
          onClick={() => {
            if (view === 'history') {
              setView('overview');
              setSelectedHistoryKey(null);
            } else {
              navigate(-1);
            }
          }}
          className="inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeft size={16} /> Retour
        </button>
        {view === 'overview' && (
          <div className="mt-2">
            <div className="text-3xl md:text-4xl font-bold">{displayPatient.nip}</div>
          </div>
        )}
      </div>

      {view === 'history' && (
        <div>
          <div className="mt-2 text-white">
            <div className="flex items-end gap-3 flex-wrap">
              <h1 className="text-3xl md:text-4xl font-bold">
                Historique traitement
                {selectedHistoryLabel && (
                  <span className="ml-2 font-normal opacity-95 text-2xl md:text-3xl"> – {selectedHistoryLabel}</span>
                )}
              </h1>
              <div className="opacity-90 text-sm">DR  GAHITO Nukounklo</div>
            </div>
            {currentHistoryEntry && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {currentHistoryEntry.statutConsultation && (
                  <span className="rounded-full bg-white/20 px-3 py-1 font-semibold text-white">
                    Statut: {currentHistoryEntry.statutConsultation}
                  </span>
                )}
                {isCurrentConsultationOpen && isCurrentConsultationAuthor && (
                  <button
                    type="button"
                    title="Compléter la consultation (Résultats & Ordonnances)"
                    onClick={openCompleteConsultationModal}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold text-[#00553E] bg-white hover:bg-[#DCEFE9] transition-colors"
                    aria-label="Compléter la consultation"
                  >
                    <Pencil size={12} />
                    Compléter
                  </button>
                )}
                {isCurrentConsultationOpen && !isCurrentConsultationAuthor && (
                  <span
                    className="rounded-full bg-white/20 px-3 py-1 font-semibold text-white"
                    title="Seul le professionnel qui a ouvert cette consultation peut la compléter/clôturer"
                  >
                    Ouverte par un autre professionnel
                  </span>
                )}
                {currentHistoryEntry.etatSortie && (
                  <span className="rounded-full bg-white/20 px-3 py-1 font-semibold text-white">
                    Sortie: {currentHistoryEntry.etatSortie}
                  </span>
                )}
                {currentHistoryEntry.dateCloture && (
                  <span className="rounded-full bg-white/20 px-3 py-1 font-semibold text-white">
                    Clôturée: {currentHistoryEntry.dateCloture}
                  </span>
                )}
              </div>
            )}
            <div className="mt-4 flex items-center gap-4 flex-wrap">
              {[
                { id: 'observations', label: 'Observations' },
                { id: 'analyses', label: 'Analyses' },
                { id: 'resultats', label: 'Résultats' },
                { id: 'ordonnances', label: 'Ordonnances' },
              ].map((t) => {
                const active = activeTab === (t.id as typeof activeTab);
                const baseStyle = active
                  ? { background: '#00553E', borderColor: '#00553E' }
                  : { background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)', borderColor: 'rgba(255,255,255,0.75)' };
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id as typeof activeTab)}
                    className={`h-11 px-5 rounded-[6px] border font-semibold text-white`}
                    style={baseStyle}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {canCloseCurrentConsultation && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowCloseConfirm(true)}
                  disabled={closingHistoryConsultation}
                  className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                >
                  {closingHistoryConsultation ? "Clôture..." : "Clôturer cette consultation"}
                </button>
              </div>
            )}

            {showCloseConfirm && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                aria-modal="true"
                role="dialog"
                aria-labelledby="modal-close-confirm-title"
              >
                <div
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                  onClick={() => setShowCloseConfirm(false)}
                  aria-hidden="true"
                />
                <div
                  className="relative w-full max-w-md rounded-[12px] bg-white p-6 shadow-xl text-[#0B1F16]"
                  style={{ border: '1px solid rgba(0,0,0,0.08)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 id="modal-close-confirm-title" className="text-lg font-bold mb-3">
                    Clôturer sans remplir les champs restants ?
                  </h2>
                  <p className="text-sm text-[#0B1F16]/70 mb-6">
                    Cette consultation n&apos;a pas encore de diagnostic, résultats ni ordonnance confirmés.
                    Voulez-vous vraiment la clôturer telle quelle, ou préférez-vous d&apos;abord compléter ces
                    informations ?
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCloseConfirm(false);
                        openCompleteConsultationModal();
                      }}
                      className="h-10 px-4 rounded-[6px] font-semibold text-white"
                      style={{ background: '#00553E' }}
                    >
                      Remplir les champs manquants
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCloseConfirm(false);
                        void handleCloseFromHistory({ allowEmpty: true });
                      }}
                      className="h-10 px-4 rounded-[6px] border font-semibold text-[#0B1F16]"
                      style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                    >
                      Clôturer sans remplir
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 rounded-[10px] border bg-white p-6" style={{ borderColor: '#E5E7EB' }}>
            {activeTab === 'resultats' ? (
              <div className="text-[#0B1F16]">
                {currentHistoryEntry && !isCurrentConsultationOpen ? (
                  <>
                    {currentHistoryEntry.resultats ? (
                      <>
                        <div className="font-semibold">Diagnostic / Résultats</div>
                        <div className="mt-2 rounded-[8px] px-4 py-3 font-medium bg-[#DAF1E9]" style={{ color: '#0B1F16' }}>
                          {currentHistoryEntry.resultats.disease}
                        </div>
                        <div className="mt-4 rounded-[8px] p-4 bg-[#DAF1E9]">
                          <p className="whitespace-pre-wrap">{currentHistoryEntry.resultats.note}</p>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={() => void handleDownloadConsultationPdf()}
                            disabled={consultationPdfLoading || !currentHistoryEntry.id}
                            className="h-11 px-4 rounded-[6px] border inline-flex items-center gap-2 font-semibold disabled:opacity-60"
                            style={{ borderColor: '#0B1F16', color: '#0B1F16', background: 'white' }}
                          >
                            <Download size={18} />
                            {consultationPdfLoading ? "Génération…" : "Télécharger la fiche de consultation"}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDownloadOrdonnancePdf()}
                            disabled={ordonnancePdfLoading || historyPharmacyOrdoLoading || !historyOrdonnanceId}
                            title={
                              !historyOrdonnanceId && !historyPharmacyOrdoLoading
                                ? "Aucune ordonnance enregistrée pour cette consultation"
                                : undefined
                            }
                            className="h-11 px-4 rounded-[6px] border inline-flex items-center gap-2 font-semibold disabled:opacity-60"
                            style={{ borderColor: '#0B1F16', color: '#0B1F16', background: 'white' }}
                          >
                            <Download size={18} />
                            {ordonnancePdfLoading ? "Génération…" : "Télécharger l'ordonnance"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                        Aucun résultat enregistré pour cette consultation.
                      </div>
                    )}
                  </>
                ) : currentHistoryEntry && isCurrentConsultationOpen && !isCurrentConsultationAuthor ? (
                  <div className="rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                    Cette consultation est ouverte par un autre professionnel. Seul son auteur peut renseigner les résultats de clôture.
                  </div>
                ) : (
                  <>
                <div className="font-semibold">Résultats de clôture</div>
                <div className="mt-3 max-w-sm">
                  <select
                    value={diseaseAutreMode ? 'autre' : (disease === 'Maladie' ? '' : disease)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'autre') {
                        setDiseaseAutreMode(true);
                        setDisease('');
                      } else {
                        setDiseaseAutreMode(false);
                        setDisease(v);
                      }
                    }}
                    disabled={loadingMaladies}
                    className="h-10 w-full rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 disabled:opacity-70"
                    style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                  >
                    <option value="">{loadingMaladies ? "Chargement…" : "Sélectionner une maladie"}</option>
                    {maladiesReferentiel.map((nom) => (
                      <option key={nom} value={nom}>{nom}</option>
                    ))}
                    <option value="autre">Autre</option>
                  </select>
                  {diseaseAutreMode && (
                    <input
                      type="text"
                      value={disease === 'Maladie' ? '' : disease}
                      onChange={(e) => setDisease(e.target.value.toUpperCase())}
                      placeholder="EX. GRIPPE, ANGINE BACTÉRIENNE..."
                      className="mt-2 h-10 w-full rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: 'rgba(0,0,0,0.15)', textTransform: 'uppercase' }}
                    />
                  )}
                </div>
                <div className="mt-4 rounded-[8px] p-0" style={{ background: '#DAF1E9' }}>
                  <textarea
                    value={resultNote}
                    onChange={(e) => setResultNote(e.target.value)}
                    placeholder="Note..."
                    className="w-full min-h-[160px] rounded-[8px] p-4 bg-transparent outline-none text-[#0B1F16]"
                  />
                </div>
                <p className="mt-3 text-xs text-[#0B1F16]/50">
                  Le téléchargement (fiche + ordonnance) sera disponible une fois la consultation clôturée.
                </p>
                  </>
                )}
              </div>
            ) : activeTab === 'analyses' ? (
              <div className="text-[#0B1F16]">
                <div className="font-semibold">Analyses effectuées</div>
                {currentHistoryEntry ? (
                  currentHistoryEntry.analyses && currentHistoryEntry.analyses.length > 0 ? (
                    <div className="mt-4 rounded-[8px] p-5" style={{ background: '#DAF1E9' }}>
                      <ul className="list-disc pl-5 space-y-2">
                        {currentHistoryEntry.analyses.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                      Aucune analyse enregistrée pour cette consultation.
                    </div>
                  )
                ) : null}
                {currentHistoryEntry && (
                  <div className="mt-5">
                    <div className="font-semibold">Pièces jointes</div>
                    {currentHistoryEntry.documents && currentHistoryEntry.documents.length > 0 ? (
                      <ul className="mt-3 space-y-2 rounded-[8px] p-5" style={{ background: '#DAF1E9' }}>
                        {currentHistoryEntry.documents.map((doc, idx) => (
                          <li key={idx}>
                            <FileAttachmentLink file={doc} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-3 rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                        Aucune pièce jointe pour cette consultation.
                      </div>
                    )}
                  </div>
                )}
                {!currentHistoryEntry && (
                  <>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      setAnalysisFiles(Array.from(e.target.files || []));
                      setUploadFileError(null);
                    }}
                  />
                  <button
                    type="button"
                    className="h-11 px-4 rounded-[6px] border font-semibold inline-flex items-center gap-3"
                    style={{ borderColor: '#0B1F16', color: '#0B1F16', background: 'white' }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFiles}
                  >
                    Ajouter des fichiers
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: '#0B1F16', color: 'white' }}>
                      <Plus size={16} />
                    </span>
                  </button>
                  {analysisFiles.length > 0 && (
                    <button
                      type="button"
                      disabled={uploadingFiles}
                      onClick={() => void handleUploadAnalysisFiles()}
                      className="h-11 px-4 rounded-[6px] font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60"
                      style={{ background: '#00553E' }}
                    >
                      {uploadingFiles ? (
                        <>
                          <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          Envoi…
                        </>
                      ) : (
                        `Envoyer ${analysisFiles.length} fichier${analysisFiles.length > 1 ? 's' : ''}`
                      )}
                    </button>
                  )}
                </div>
                {uploadFileError && (
                  <div className="mt-2 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {uploadFileError}
                  </div>
                )}
                <div className="mt-4 rounded-[8px] p-5 space-y-2" style={{ background: '#DAF1E9' }}>
                  {sentDocs.length === 0 && pendingDocs.length === 0 && analysisFiles.length === 0 ? (
                    <p className="text-[#0B1F16]/60 text-sm">Aucun fichier joint.</p>
                  ) : (
                    <>
                      {sentDocs.length > 0 && (
                        <ul className="space-y-2">
                          {sentDocs.map((doc, idx) => (
                            <li key={idx}>
                              <FileAttachmentLink
                                file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pendingDocs.length > 0 && (
                        <ul className="space-y-2">
                          {pendingDocs.map((doc, idx) => (
                            <li key={idx}>
                              <FileAttachmentLink
                                file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                                trailing={
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="text-xs font-medium text-amber-600">en attente</span>
                                    <button
                                      type="button"
                                      onClick={() => setPendingDocs((prev) => prev.filter((_, i) => i !== idx))}
                                      className="text-red-500 hover:text-red-700"
                                      aria-label="Retirer"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {analysisFiles.length > 0 && (
                        <ul className="space-y-1 text-sm text-[#0B1F16]/70 mt-1">
                          {analysisFiles.map((f, idx) => (
                            <li key={idx}>{f.name} <span className="text-[#0B1F16]/40">({Math.round(f.size / 1024)} Ko) — en attente d'envoi</span></li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
                  </>
                )}
              </div>
            ) : activeTab === 'ordonnances' ? (
              <div className="text-[#0B1F16]">
                {currentHistoryEntry && !isCurrentConsultationOpen ? (
                  currentHistoryEntry.ordonnances && currentHistoryEntry.ordonnances.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="font-semibold">MEDICAMENTS</div>
                      <div className="font-semibold">POSOLOGIE</div>
                      <div className="font-semibold">RENOUVELABLE</div>
                    </div>
                    <div className="mt-3 rounded-[8px] p-0 overflow-hidden" style={{ background: '#DAF1E9' }}>
                      <div className="h-[1px]" style={{ background: 'rgba(0,0,0,0.1)' }} />
                      {currentHistoryEntry.ordonnances.map((row, idx) => (
                        <div key={idx} className="grid grid-cols-3 gap-0 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                          <div className="p-3 font-semibold">{row.medicament}</div>
                          <div className="p-3">{row.note}</div>
                          <div className="p-3">{row.renouvelable ? "Renouvelable" : "Non renouvelable"}</div>
                        </div>
                      ))}
                      <div className="p-6" />
                    </div>
                    {currentHistoryEntry.id && (
                      <>
                        {historyPharmacyOrdoLoading && (
                          <p className="mt-5 text-sm text-[#0B1F16]/60">Chargement des pharmacies et des ordonnances…</p>
                        )}
                        {historyPharmacyOrdoError && !historyPharmacyOrdoLoading && (
                          <div className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2">
                            {historyPharmacyOrdoError}
                          </div>
                        )}
                        {!historyPharmacyOrdoLoading && !historyPharmacyOrdoError && (
                          <PharmacyOrdonnanceInline
                            ordonnanceOptions={historyPharmacyOrdoOptions}
                            onSent={() => showToast("Ordonnance envoyée à la pharmacie.", "success")}
                          />
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                    Aucune ordonnance pour cette consultation.
                  </div>
                )
                ) : currentHistoryEntry && isCurrentConsultationOpen && !isCurrentConsultationAuthor ? (
                  <div className="rounded-[8px] p-5 text-[#0B1F16]/70" style={{ background: '#DAF1E9' }}>
                    Cette consultation est ouverte par un autre professionnel. Seul son auteur peut modifier les ordonnances.
                  </div>
                ) : (
                  <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="font-semibold">MEDICAMENTS</div>
                  <div className="font-semibold">POSOLOGIE</div>
                  <div className="font-semibold">RENOUVELABLE</div>
                </div>
                <div className="mt-3 rounded-[8px] p-0 overflow-hidden" style={{ background: '#DAF1E9' }}>
                  <div className="h-[1px]" style={{ background: 'rgba(0,0,0,0.1)' }} />
                  {prescriptions.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-0 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                      <div className="p-3">
                        <input
                          value={row.medicament}
                          onChange={(e) => setPrescriptions((prev) => prev.map((r, i) => i === idx ? { ...r, medicament: e.target.value } : r))}
                          className="w-full bg-transparent outline-none font-semibold"
                          placeholder="medicaments"
                        />
                      </div>
                      <div className="p-3">
                        <input
                          value={row.note}
                          onChange={(e) => setPrescriptions((prev) => prev.map((r, i) => i === idx ? { ...r, note: e.target.value } : r))}
                          className="w-full bg-transparent outline-none"
                          placeholder="note..."
                        />
                      </div>
                      <div className="p-3">
                        <select
                          value={row.renouvelable ? "renouvelable" : "non_renouvelable"}
                          onChange={(e) => setPrescriptions((prev) => prev.map((r, i) => i === idx ? { ...r, renouvelable: e.target.value === "renouvelable" } : r))}
                          className="w-full bg-transparent outline-none text-sm"
                        >
                          <option value="non_renouvelable">Non renouvelable</option>
                          <option value="renouvelable">Renouvelable</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  <div className="p-6" />
                </div>
                <div className="mt-4">
                  <button
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                    style={{ background: '#00553E', color: 'white' }}
                    onClick={() => setPrescriptions((prev) => [...prev, { medicament: '', note: '', renouvelable: false }])}
                    aria-label="Ajouter une ordonnance"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                  </>
                )}
              </div>
            ) : (
              <div className="rounded-[8px] p-5 text-[#0B1F16]" style={{ background: '#DAF1E9' }}>
                {currentHistoryEntry?.observations ? (
                  <p className="whitespace-pre-wrap">{currentHistoryEntry.observations}</p>
                ) : (
                  <p className="text-[#0B1F16]/60">
                    Aucune observation enregistrée pour cette consultation.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'overview' && (
        <div>
          {/* Deux colonnes: carte détails + historique patient */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Carte Détails (à gauche) */}
            <div
              className="rounded-[10px] p-5 flex flex-col gap-4"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              {/* En-tête NIP */}
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 text-white font-bold text-sm"
                  style={{ background: "#00553E" }}
                >
                  P
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0B1F16]/50">NIP</div>
                  <div className="text-lg font-extrabold text-[#0B1F16] tabular-nums tracking-wide">{displayPatient.nip}</div>
                </div>
              </div>

              <div className="h-px bg-black/5" />

              {/* Grille d'infos */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-[#0B1F16]">
                {[
                  { label: "Âge", value: displayPatient.age },
                  { label: "Naissance", value: displayPatient.birth },
                  { label: "Téléphone", value: displayPatient.phone },
                  { label: "Taille", value: displayPatient.height },
                  { label: "Groupe sanguin", value: displayPatient.blood },
                  { label: "Profession", value: displayPatient.job ? displayPatient.job.replace(/_/g, " ") : "—" },
                ].map(({ label, value }) => (
                  <div key={label} className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0B1F16]/45 mb-0.5">{label}</div>
                    <div className="text-sm font-medium truncate">{value || "—"}</div>
                  </div>
                ))}
              </div>

              {(displayPatient.address || displayPatient.antecedents) && (
                <>
                  <div className="h-px bg-black/5" />
                  <div className="grid grid-cols-1 gap-3 text-[#0B1F16]">
                    {displayPatient.address && (
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0B1F16]/45 mb-0.5">Adresse</div>
                        <div className="text-sm font-medium">{displayPatient.address}</div>
                      </div>
                    )}
                    {displayPatient.antecedents && (
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0B1F16]/45 mb-0.5">Antécédents</div>
                        <div className="text-sm font-medium">{displayPatient.antecedents}</div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {medRaw && (
                <>
                  <div className="h-px bg-black/5" />
                  {!editingMed ? (
                    <button
                      type="button"
                      onClick={startEditMed}
                      className="inline-flex w-fit items-center gap-2 h-10 px-4 rounded-[8px] text-sm font-semibold text-white shadow-sm hover:opacity-90"
                      style={{ background: "#00553E" }}
                    >
                      <Pencil size={14} /> Modifier le dossier médical
                    </button>
                  ) : (
                    medForm && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="mb-1 block text-xs text-[#0B1F16]/60">Groupe sanguin</label>
                            <Select
                              value={medForm.groupeSanguin || UNSET_OPTION}
                              onValueChange={(v) => updateMedField("groupeSanguin", v === UNSET_OPTION ? "" : v)}
                            >
                              <SelectTrigger className="h-9 bg-white text-sm">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNSET_OPTION}>—</SelectItem>
                                {withCurrentValue(BLOOD_GROUP_OPTIONS, medForm.groupeSanguin).map((g) => (
                                  <SelectItem key={g} value={g}>{g}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-[#0B1F16]/60">Électrophorèse</label>
                            <Select
                              value={medForm.electrophorese || UNSET_OPTION}
                              onValueChange={(v) => updateMedField("electrophorese", v === UNSET_OPTION ? "" : v)}
                            >
                              <SelectTrigger className="h-9 bg-white text-sm">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNSET_OPTION}>—</SelectItem>
                                {withCurrentValue(ELECTROPHORESE_OPTIONS, medForm.electrophorese).map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-[#0B1F16]/60">Poids (kg)</label>
                            <input
                              type="number"
                              value={medForm.poids ?? ""}
                              onChange={(e) => updateMedField("poids", e.target.value === "" ? null : Number(e.target.value))}
                              className="h-9 w-full rounded-[8px] border bg-white px-3 text-sm outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-[#0B1F16]/60">Taille (cm)</label>
                            <input
                              type="number"
                              value={medForm.taille ?? ""}
                              onChange={(e) => updateMedField("taille", e.target.value === "" ? null : Number(e.target.value))}
                              className="h-9 w-full rounded-[8px] border bg-white px-3 text-sm outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-1.5 block text-xs text-[#0B1F16]/60">Allergies</label>
                          {medForm.allergies.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-1.5">
                              {medForm.allergies.map((a, i) => (
                                <span
                                  key={i}
                                  className="flex items-center gap-1 rounded-full bg-red-100 py-0.5 pl-2 pr-1 text-xs text-red-700"
                                >
                                  {a}
                                  <button
                                    type="button"
                                    onClick={() => removeAllergie(i)}
                                    aria-label={`Supprimer l'allergie ${a}`}
                                    className="rounded-full p-0.5 hover:bg-red-200"
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={newAllergie}
                              onChange={(e) => setNewAllergie(e.target.value.toUpperCase())}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); addAllergie(); }
                              }}
                              placeholder="AJOUTER UNE ALLERGIE"
                              className="h-9 flex-1 rounded-[8px] border bg-white px-3 text-sm outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                            <button
                              type="button"
                              onClick={addAllergie}
                              disabled={!newAllergie.trim()}
                              className="flex h-9 w-9 items-center justify-center rounded-[8px] border text-[#00553E] disabled:opacity-40"
                              style={{ borderColor: "#D1D5DB" }}
                              aria-label="Ajouter l'allergie"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="mb-1.5 block text-xs text-[#0B1F16]/60">Intolérances</label>
                          {medForm.intolerances.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-1.5">
                              {medForm.intolerances.map((t, i) => (
                                <span
                                  key={i}
                                  className="flex items-center gap-1 rounded-full bg-blue-100 py-0.5 pl-2 pr-1 text-xs text-blue-700"
                                >
                                  {t}
                                  <button
                                    type="button"
                                    onClick={() => removeIntolerance(i)}
                                    aria-label={`Supprimer l'intolérance ${t}`}
                                    className="rounded-full p-0.5 hover:bg-blue-200"
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={newIntolerance}
                              onChange={(e) => setNewIntolerance(e.target.value.toUpperCase())}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); addIntolerance(); }
                              }}
                              placeholder="AJOUTER UNE INTOLÉRANCE"
                              className="h-9 flex-1 rounded-[8px] border bg-white px-3 text-sm outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                            <button
                              type="button"
                              onClick={addIntolerance}
                              disabled={!newIntolerance.trim()}
                              className="flex h-9 w-9 items-center justify-center rounded-[8px] border text-[#00553E] disabled:opacity-40"
                              style={{ borderColor: "#D1D5DB" }}
                              aria-label="Ajouter l'intolérance"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <div className="mb-1.5 flex items-center justify-between">
                            <span className="text-xs text-[#0B1F16]/60">Antécédents</span>
                            <button
                              type="button"
                              onClick={() =>
                                setMedForm((prev) =>
                                  prev ? { ...prev, antecedents: [...prev.antecedents, emptyAntecedent()] } : prev
                                )
                              }
                              className="flex items-center gap-1 text-xs font-medium text-[#00553E] hover:underline"
                            >
                              <Plus size={12} /> Ajouter
                            </button>
                          </div>
                          {medForm.antecedents.length === 0 && (
                            <p className="text-xs text-[#0B1F16]/40">Aucun antécédent.</p>
                          )}
                          <div className="space-y-2">
                            {medForm.antecedents.map((a, i) => (
                              <div key={i} className="grid grid-cols-1 gap-2 rounded-[8px] border p-2 sm:grid-cols-[1fr_2fr_auto_auto]" style={{ borderColor: "#E5E7EB" }}>
                                <input
                                  placeholder="Type"
                                  value={a.type}
                                  onChange={(e) => updateAntecedent(i, { type: e.target.value.toUpperCase() })}
                                  className="h-8 rounded-[6px] border bg-white px-2 text-xs outline-none focus:border-[#00553E]"
                                  style={{ borderColor: "#D1D5DB" }}
                                />
                                <input
                                  placeholder="Description"
                                  value={a.description}
                                  onChange={(e) => updateAntecedent(i, { description: e.target.value.toUpperCase() })}
                                  className="h-8 rounded-[6px] border bg-white px-2 text-xs outline-none focus:border-[#00553E]"
                                  style={{ borderColor: "#D1D5DB" }}
                                />
                                <input
                                  type="date"
                                  value={dateInputValue(a.date)}
                                  onChange={(e) => updateAntecedent(i, { date: e.target.value || null })}
                                  className="h-8 rounded-[6px] border bg-white px-2 text-xs outline-none focus:border-[#00553E]"
                                  style={{ borderColor: "#D1D5DB" }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAntecedent(i)}
                                  className="flex items-center justify-center rounded-[6px] text-red-500 hover:bg-red-50"
                                  aria-label="Supprimer l'antécédent"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {medEditError && (
                          <p className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                            {medEditError}
                          </p>
                        )}

                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={cancelEditMed}
                            disabled={savingMed}
                            className="h-9 rounded-[8px] border px-4 text-xs font-medium text-[#0B1F16] disabled:opacity-50"
                            style={{ borderColor: "#E5E7EB" }}
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleSaveMed()}
                            disabled={savingMed}
                            className="h-9 rounded-[8px] px-4 text-xs font-semibold text-white disabled:opacity-60"
                            style={{ background: "#00553E" }}
                          >
                            {savingMed ? "Enregistrement…" : "Enregistrer"}
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </>
              )}
            </div>

            {/* Historique patient (à droite) */}
            <div
              className="rounded-[10px] p-6"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <div className="text-[#0B1F16] text-2xl font-bold">Historique patient</div>
              <div
                className="mt-6 space-y-6 overflow-y-auto overflow-x-hidden pr-1"
                style={{ maxHeight: '22rem' }}
              >
                {historyEntries.map((item) => (
                  <button
                    key={item.selectionKey}
                    type="button"
                    onClick={() => {
                      setSelectedHistoryKey(item.selectionKey);
                      setView('history');
                    }}
                    className="w-full text-left relative pl-5 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40 focus:ring-offset-2"
                  >
                    <span className="absolute left-0 top-1 w-2 h-2 rounded-full" style={{ background: "#0CA37F" }} />
                    <div>
                      <span
                        className="inline-block px-3 py-1 rounded-[6px] text-[#0B1F16] font-semibold transition-colors bg-[rgba(12,163,127,0.12)] hover:bg-[rgba(12,163,127,0.22)]"
                      >
                        {item.label}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[#0B1F16]">
                      <span>{item.doctor}</span>
                      {proNip && item.professionnelNip === proNip && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={{ background: "rgba(12,163,127,0.15)", color: "#00553E" }}
                        >
                          Vous
                        </span>
                      )}
                    </div>
                    {item.statutConsultation && (
                      <div className="mt-1">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            item.statutConsultation === "fermee"
                              ? "bg-[#DCEFE9] text-[#00553E]"
                              : item.statutConsultation === "ouverte"
                                ? "bg-amber-100 text-amber-900"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.statutConsultation}
                        </span>
                      </div>
                    )}
                    {item.motif && (
                      <div className="mt-1.5 text-sm text-[#0B1F16]/75">{item.motif}</div>
                    )}
                    {item.ordonnances && item.ordonnances.length > 0 && (
                      <div className="mt-1 text-xs text-[#0B1F16]/60">
                        {item.ordonnances.length} médicament(s) • {item.analyses?.length ?? 0} analyse(s)
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    resetConsultationForm();
                    setShowAddConsultation(true);
                  }}
                  className="h-10 px-4 rounded-[6px] font-semibold border"
                  style={{ borderColor: "rgba(0,0,0,0.12)", color: "#0B1F16", background: "white" }}
                >
                  Ajouter une consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {view === 'overview' && (
        <div className="mt-6">
          <div
            className="h-14 px-4 rounded-[10px] flex items-center justify-between"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.45)",
            }}
          >
            <div className="text-2xl font-bold text-white">Vaccination</div>
            <button
              type="button"
              onClick={() => setShowAddVaccination(true)}
              className="inline-flex items-center gap-2 text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1"
              title="Ajouter une vaccination"
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          {showAddVaccination && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              aria-modal="true"
              role="dialog"
              aria-labelledby="modal-vaccination-title"
            >
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => {
                  setShowAddVaccination(false);
                  setNewVaccination({ nom: '', date: '', statut: 'Effectué' });
                }}
                aria-hidden="true"
              />
              <div
                className="relative w-full max-w-md rounded-[12px] bg-white p-6 shadow-xl text-[#0B1F16]"
                style={{ border: '1px solid rgba(0,0,0,0.08)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 id="modal-vaccination-title" className="text-xl font-bold">
                    Nouvelle vaccination
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddVaccination(false);
                      setNewVaccination({ nom: '', date: '', statut: 'Effectué' });
                    }}
                    className="p-1.5 rounded-[6px] text-[#0B1F16]/70 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                    aria-label="Fermer"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-1">Vaccin</label>
                    <input
                      type="text"
                      value={newVaccination.nom}
                      onChange={(e) => setNewVaccination((p) => ({ ...p, nom: e.target.value }))}
                      placeholder="ex. Polio, BCG, COVID-19"
                      className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-1">Date</label>
                    <input
                      type="date"
                      value={newVaccination.date}
                      onChange={(e) => setNewVaccination((p) => ({ ...p, date: e.target.value }))}
                      className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-1">Statut</label>
                    <select
                      value={newVaccination.statut}
                      onChange={(e) => setNewVaccination((p) => ({ ...p, statut: e.target.value }))}
                      className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                    >
                      <option value="Effectué">Effectué</option>
                      <option value="Prévu">Prévu</option>
                      <option value="Reporté">Reporté</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddVaccination(false);
                      setNewVaccination({ nom: '', date: '', statut: 'Effectué' });
                    }}
                    className="h-10 px-4 rounded-[6px] font-semibold border text-[#0B1F16] bg-white"
                    style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleAddVaccination}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white"
                    style={{ background: '#00553E' }}
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 space-y-3">
            {vaccinations.map((v) => (
              <div
                key={v.id}
                className="rounded-[10px] p-4 flex items-center justify-between text-[#0B1F16] flex-wrap gap-2"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <div className="flex-1 min-w-0 font-medium">{v.nom}</div>
                <div className="w-40 text-right shrink-0 text-sm text-[#0B1F16]/70">{v.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Ajout consultation (multistep) */}
      {showAddConsultation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
          aria-labelledby="modal-consultation-title"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowAddConsultation(false);
              resetConsultationForm();
            }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg rounded-[12px] bg-white shadow-xl text-[#0B1F16] max-h-[90vh] flex flex-col"
            style={{ border: '1px solid rgba(0,0,0,0.08)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b shrink-0" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 id="modal-consultation-title" className="text-xl font-bold">
                  Nouvelle consultation
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddConsultation(false);
                    resetConsultationForm();
                  }}
                  className="p-1.5 rounded-[6px] text-[#0B1F16]/70 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                  aria-label="Fermer"
                >
                  <X size={20} />
                </button>
              </div>
              {(draftConsultationId || draftConsultationStatus) && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                  {draftConsultationStatus && (
                    <span
                      className={`rounded-full px-3 py-1 font-semibold ${
                        draftConsultationStatus === "fermee"
                          ? "bg-[#DCEFE9] text-[#00553E]"
                          : draftConsultationStatus === "ouverte"
                            ? "bg-amber-100 text-amber-900"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {draftConsultationStatus}
                    </span>
                  )}
                  {draftConsultationId && (
                    <span className="rounded-full bg-black/5 px-3 py-1 font-semibold text-[#0B1F16]/80">
                      ID: {draftConsultationId}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                {CONSULTATION_STEPS.map((step, idx) => (
                  <span key={step.id} className="inline-flex items-center gap-1">
                    <span
                      className={`text-sm font-medium px-2 py-1 rounded ${
                        consultationStep === idx ? 'bg-[#00553E] text-white' : 'bg-black/5 text-[#0B1F16]/70'
                      }`}
                    >
                      {idx + 1}. {step.label}
                    </span>
                    {idx < CONSULTATION_STEPS.length - 1 && (
                      <ChevronRight size={16} className="text-[#0B1F16]/40" />
                    )}
                  </span>
                ))}
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-5">
              {consultationStep === 0 && (
                <>
                  {!draftConsultationId && (
                    <div>
                      <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                        Établissement (ou cabinet) sous lequel créer la consultation <span className="text-red-500">*</span>
                      </label>
                      {monEtablissementsLoading ? (
                        <p className="text-sm text-[#0B1F16]/60">Chargement de vos établissements…</p>
                      ) : monEtablissements.length === 0 ? (
                        <div className="rounded-[8px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                          Vous n'avez ni cabinet propre ni liaison active avec un établissement. Rendez-vous dans votre
                          profil pour en créer un avant de créer une consultation.
                        </div>
                      ) : (
                        <select
                          value={selectedEtablissementId}
                          onChange={(e) => setSelectedEtablissementId(e.target.value)}
                          className="w-full h-10 rounded-[8px] border bg-white px-3"
                          style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                        >
                          <option value="">Sélectionner…</option>
                          {monEtablissements.map((item) => (
                            <option key={item.liaison.id} value={item.etablissement.id}>
                              {item.etablissement.nom}
                              {item.liaison.source === 'cabinet_auto' ? ' (mon cabinet)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                      Observations <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={newConsultation.observations}
                      onChange={(e) => setNewConsultation((p) => ({ ...p, observations: e.target.value.toUpperCase() }))}
                      placeholder="SAISIR LES OBSERVATIONS DE LA CONSULTATION..."
                      className="w-full min-h-[140px] rounded-[8px] px-3 py-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 resize-y"
                      style={{
                        borderColor: newConsultation.observations.trim() ? 'rgba(0,0,0,0.15)' : 'rgba(239,68,68,0.4)',
                        textTransform: 'uppercase',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Analyses effectuées ou demandées</label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newConsultation.analysisInput}
                        onChange={(e) => setNewConsultation((p) => ({ ...p, analysisInput: e.target.value.toUpperCase() }))}
                        placeholder="EX. NFS, GLYCÉMIE..."
                        className="flex-1 h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                        style={{ borderColor: 'rgba(0,0,0,0.15)', textTransform: 'uppercase' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = newConsultation.analysisInput.trim();
                            if (v) setNewConsultation((p) => ({ ...p, analyses: [...p.analyses, v], analysisInput: '' }));
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const v = newConsultation.analysisInput.trim();
                          if (v) setNewConsultation((p) => ({ ...p, analyses: [...p.analyses, v], analysisInput: '' }));
                        }}
                        className="h-10 px-4 rounded-[6px] font-semibold text-white shrink-0"
                        style={{ background: '#00553E' }}
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                    {newConsultation.analyses.length > 0 && (
                      <ul className="space-y-1.5 rounded-[8px] p-3 bg-[#DAF1E9]/50">
                        {newConsultation.analyses.map((name, idx) => (
                          <li
                            key={idx}
                            className="flex w-full items-center justify-between gap-2 rounded-[6px] bg-white px-3 py-2"
                            style={{ border: '1px solid rgba(0,0,0,0.06)' }}
                          >
                            <span className="truncate text-sm text-[#0B1F16]">{name}</span>
                            <button
                              type="button"
                              onClick={() => setNewConsultation((p) => ({ ...p, analyses: p.analyses.filter((_, i) => i !== idx) }))}
                              className="text-red-500 hover:text-red-700 shrink-0"
                              aria-label="Retirer"
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Pièces jointes */}
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                      Pièces jointes
                    </label>
                    <div className="flex gap-2 items-center">
                      <label
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-[6px] border text-sm font-medium text-[#0B1F16] bg-white cursor-pointer hover:bg-[#F4FAF8]"
                        style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                      >
                        <Paperclip size={16} />
                        Choisir des fichiers
                        <input
                          ref={modalFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            if (files.length) setAnalysisFiles((prev) => [...prev, ...files]);
                          }}
                        />
                      </label>
                      {analysisFiles.length > 0 && (
                        <button
                          type="button"
                          disabled={uploadingFiles}
                          onClick={() => void handleUploadAnalysisFiles()}
                          className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                          style={{ background: '#00553E' }}
                        >
                          {uploadingFiles ? "Envoi…" : `Envoyer (${analysisFiles.length})`}
                        </button>
                      )}
                    </div>

                    {/* Fichiers sélectionnés (en attente d'envoi) */}
                    {analysisFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {analysisFiles.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-[#0B1F16]/70">
                            <Paperclip size={13} className="shrink-0" />
                            <span className="truncate">{f.name}</span>
                            <button
                              type="button"
                              onClick={() => setAnalysisFiles((prev) => prev.filter((_, idx) => idx !== i))}
                              className="ml-auto text-red-500 hover:text-red-700 shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {uploadFileError && (
                      <p className="mt-2 text-sm text-red-600">{uploadFileError}</p>
                    )}

                    {/* Docs en attente (bucket OK, pas encore envoyés au backend — pas de consultationId) */}
                    {pendingDocs.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {pendingDocs.map((doc, i) => (
                          <li key={i}>
                            <FileAttachmentLink
                              file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                              trailing={
                                <div className="flex shrink-0 items-center gap-2">
                                  <span className="text-xs font-medium text-amber-600">en attente</span>
                                  <button
                                    type="button"
                                    onClick={() => setPendingDocs((prev) => prev.filter((_, idx) => idx !== i))}
                                    className="text-red-500 hover:text-red-700"
                                    aria-label="Retirer"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              }
                            />
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Docs envoyés au backend avec succès */}
                    {sentDocs.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-[#00553E] font-medium">
                          {sentDocs.length} fichier{sentDocs.length > 1 ? "s" : ""} joint{sentDocs.length > 1 ? "s" : ""} à la consultation.
                        </p>
                        <ul className="mt-2 space-y-2">
                          {sentDocs.map((doc, i) => (
                            <li key={i}>
                              <FileAttachmentLink
                                file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
              {consultationStep === 1 && (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Diagnostic / Maladie</label>
                      <select
                        value={newConsultationDiseaseAutreMode ? 'autre' : (newConsultation.disease === 'Maladie' ? '' : newConsultation.disease)}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'autre') {
                            setNewConsultationDiseaseAutreMode(true);
                            setNewConsultation((p) => ({ ...p, disease: '' }));
                          } else {
                            setNewConsultationDiseaseAutreMode(false);
                            setNewConsultation((p) => ({ ...p, disease: v }));
                          }
                        }}
                        disabled={loadingMaladies}
                        className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 disabled:opacity-70"
                        style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                      >
                        <option value="">{loadingMaladies ? "Chargement…" : "Sélectionner une maladie"}</option>
                        {maladiesReferentiel.map((nom) => (
                          <option key={nom} value={nom}>{nom}</option>
                        ))}
                        <option value="autre">Autre</option>
                      </select>
                      {newConsultationDiseaseAutreMode && (
                        <input
                          type="text"
                          value={newConsultation.disease === 'Maladie' ? '' : newConsultation.disease}
                          onChange={(e) => setNewConsultation((p) => ({ ...p, disease: e.target.value.toUpperCase() }))}
                          placeholder="EX. GRIPPE, ANGINE BACTÉRIENNE..."
                          className="mt-2 w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                          style={{ borderColor: 'rgba(0,0,0,0.15)', textTransform: 'uppercase' }}
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">État de sortie</label>
                      <select
                        value={etatSortie}
                        onChange={(e) => setEtatSortie(e.target.value as ConsultationExitState)}
                        className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                        style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                      >
                        {CONSULTATION_EXIT_STATES.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Résultats / Note</label>
                      <textarea
                        value={newConsultation.resultNote}
                        onChange={(e) => setNewConsultation((p) => ({ ...p, resultNote: e.target.value.toUpperCase() }))}
                        placeholder="COMMENTAIRE SUR LES RÉSULTATS..."
                        className="w-full min-h-[100px] rounded-[8px] px-3 py-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 resize-y"
                        style={{ borderColor: 'rgba(0,0,0,0.15)', textTransform: 'uppercase' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Ordonnances</label>
                    <div className="grid grid-cols-[2fr_2fr_1fr] gap-4 mb-1 px-1">
                      <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Médicament</span>
                      <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Posologie</span>
                      <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Renouvelable</span>
                    </div>
                    <div className="rounded-[8px] overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                      {newConsultation.ordonnances.map((row, idx) => (
                        <div
                          key={idx}
                          className="grid grid-cols-[2fr_2fr_1fr] border-b last:border-b-0 bg-[#DAF1E9]/30"
                          style={{ borderColor: 'rgba(0,0,0,0.08)' }}
                        >
                          <input
                            value={row.medicament}
                            readOnly={draftConsultationStatus === "fermee"}
                            onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, medicament: e.target.value.toUpperCase() } : r) }))}
                            className="p-3 bg-transparent outline-none font-medium border-r"
                            style={{ borderColor: 'rgba(0,0,0,0.08)', textTransform: 'uppercase' }}
                            placeholder="MÉDICAMENT"
                          />
                          <input
                            value={row.note}
                            readOnly={draftConsultationStatus === "fermee"}
                            onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, note: e.target.value.toUpperCase() } : r) }))}
                            className="p-3 bg-transparent outline-none border-r"
                            style={{ borderColor: 'rgba(0,0,0,0.08)', textTransform: 'uppercase' }}
                            placeholder="POSOLOGIE"
                          />
                          <select
                            value={row.renouvelable ? "renouvelable" : "non_renouvelable"}
                            disabled={draftConsultationStatus === "fermee"}
                            onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, renouvelable: e.target.value === "renouvelable" } : r) }))}
                            className="p-3 bg-transparent outline-none text-sm"
                          >
                            <option value="non_renouvelable">Non renouvelable</option>
                            <option value="renouvelable">Renouvelable</option>
                          </select>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <button
                        type="button"
                        disabled={draftConsultationStatus === "fermee"}
                        onClick={() => setNewConsultation((p) => ({ ...p, ordonnances: [...p.ordonnances, { medicament: '', note: '', renouvelable: false }] }))}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold shrink-0 disabled:opacity-40"
                        style={{ background: '#00553E' }}
                        aria-label="Ajouter une ligne"
                      >
                        <Plus size={20} />
                      </button>
                      <div className="flex flex-col min-w-[min(100%,220px)] flex-1">
                        <label htmlFor="modal-consult-pharmacie" className="text-xs font-medium text-[#0B1F16]/70 mb-1">
                          Pharmacie
                        </label>
                        <select
                          id="modal-consult-pharmacie"
                          value={modalConsultSelectedPharmacieId}
                          onChange={(e) => {
                            setModalConsultSelectedPharmacieId(e.target.value);
                            setModalConsultSendError(null);
                          }}
                          disabled={modalConsultPharmaciesLoading}
                          className="h-10 rounded-[6px] px-3 border bg-white text-sm outline-none focus:ring-2 focus:ring-[#0CA37F]/40 disabled:opacity-60"
                          style={{ borderColor: "rgba(0,0,0,0.15)" }}
                        >
                          <option value="">
                            {modalConsultPharmaciesLoading ? "Chargement…" : "Choisir une pharmacie…"}
                          </option>
                          {modalConsultPharmacies.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.nomPharmacie} — {p.commune ?? p.departement ?? "—"}
                            </option>
                          ))}
                        </select>
                      </div>
                      {modalPostCloseOrdoOptions.length > 1 && (
                        <div className="flex flex-col min-w-[min(100%,200px)] flex-1">
                          <label htmlFor="modal-consult-ordo" className="text-xs font-medium text-[#0B1F16]/70 mb-1">
                            Ordonnance
                          </label>
                          <select
                            id="modal-consult-ordo"
                            value={modalConsultSelectedOrdonnanceId}
                            onChange={(e) => setModalConsultSelectedOrdonnanceId(e.target.value)}
                            disabled={modalConsultSending}
                            className="h-10 rounded-[6px] px-3 border bg-white text-sm outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                            style={{ borderColor: "rgba(0,0,0,0.15)" }}
                          >
                            {modalPostCloseOrdoOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={
                          modalConsultSending ||
                          !modalConsultSelectedPharmacieId ||
                          modalPostCloseOrdoOptions.length === 0
                        }
                        onClick={() => void handleModalSendOrdonnanceToPharmacy()}
                        className="h-10 px-4 rounded-[6px] text-sm font-semibold text-white shrink-0 disabled:opacity-50"
                        style={{ background: "#00553E" }}
                      >
                        {modalConsultSending ? "Envoi…" : "Envoyer l\u2019ordonnance"}
                      </button>
                    </div>
                    {draftConsultationStatus !== "fermee" && (
                      <p className="mt-2 text-xs text-[#0B1F16]/60">
                        Après la clôture de la consultation, vous pourrez envoyer l&apos;ordonnance à la pharmacie
                        sélectionnée.
                      </p>
                    )}
                    {draftConsultationStatus === "fermee" && modalPostCloseOrdoOptions.length === 0 && (
                      <div className="mt-2 space-y-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-[8px] px-3 py-2">
                        <p>
                          Aucun identifiant d&apos;ordonnance dans la liste API (réseau, consultation trop ancienne sans
                          ordonnance SQL, ou échec de lecture). Réessayez ou consultez l&apos;historique patient.
                        </p>
                        <button
                          type="button"
                          disabled={modalOrdoRefetching}
                          onClick={() => void handleRetryModalOrdoFetch()}
                          className="h-8 px-3 rounded-[6px] text-xs font-semibold border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                        >
                          {modalOrdoRefetching ? "Chargement…" : "Recharger les ordonnances"}
                        </button>
                      </div>
                    )}
                    {modalConsultSendError && (
                      <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2">
                        {modalConsultSendError}
                      </div>
                    )}
                  </div>

                  {/* Pièces jointes */}
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                      Pièces jointes
                    </label>
                    <div className="flex gap-2 items-center">
                      <label
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-[6px] border text-sm font-medium text-[#0B1F16] bg-white cursor-pointer hover:bg-[#F4FAF8]"
                        style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                      >
                        <Paperclip size={16} />
                        Choisir des fichiers
                        <input
                          ref={modalFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            if (files.length) setAnalysisFiles((prev) => [...prev, ...files]);
                          }}
                        />
                      </label>
                      {analysisFiles.length > 0 && (
                        <button
                          type="button"
                          disabled={uploadingFiles}
                          onClick={() => void handleUploadAnalysisFiles()}
                          className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                          style={{ background: '#00553E' }}
                        >
                          {uploadingFiles ? "Envoi…" : `Envoyer (${analysisFiles.length})`}
                        </button>
                      )}
                    </div>

                    {analysisFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {analysisFiles.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-[#0B1F16]/70">
                            <Paperclip size={13} className="shrink-0" />
                            <span className="truncate">{f.name}</span>
                            <button
                              type="button"
                              onClick={() => setAnalysisFiles((prev) => prev.filter((_, idx) => idx !== i))}
                              className="ml-auto text-red-500 hover:text-red-700 shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {uploadFileError && (
                      <p className="mt-2 text-sm text-red-600">{uploadFileError}</p>
                    )}

                    {pendingDocs.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {pendingDocs.map((doc, i) => (
                          <li key={i}>
                            <FileAttachmentLink
                              file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                              trailing={
                                <div className="flex shrink-0 items-center gap-2">
                                  <span className="text-xs font-medium text-amber-600">en attente</span>
                                  <button
                                    type="button"
                                    onClick={() => setPendingDocs((prev) => prev.filter((_, idx) => idx !== i))}
                                    className="text-red-500 hover:text-red-700"
                                    aria-label="Retirer"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              }
                            />
                          </li>
                        ))}
                      </ul>
                    )}

                    {sentDocs.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-[#00553E] font-medium">
                          {sentDocs.length} fichier{sentDocs.length > 1 ? "s" : ""} joint{sentDocs.length > 1 ? "s" : ""} à la consultation.
                        </p>
                        <ul className="mt-2 space-y-2">
                          {sentDocs.map((doc, i) => (
                            <li key={i}>
                              <FileAttachmentLink
                                file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t flex gap-2 justify-between shrink-0" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div>
                {consultationStep > 0 ? (
                  <button
                    type="button"
                    disabled={draftConsultationStatus === "fermee"}
                    onClick={() => setConsultationStep((s) => s - 1)}
                    className="h-10 px-4 rounded-[6px] font-semibold border text-[#0B1F16] bg-white disabled:opacity-40"
                    style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                  >
                    Précédent
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddConsultation(false);
                      resetConsultationForm();
                    }}
                    className="h-10 px-4 rounded-[6px] font-semibold border text-[#0B1F16] bg-white"
                    style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                  >
                    Annuler
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {consultationStep === 0 ? (
                  <>
                    <button
                      type="button"
                      disabled={
                        savingConsultation ||
                        !newConsultation.observations.trim() ||
                        (!draftConsultationId && !selectedEtablissementId)
                      }
                      onClick={() => {
                        // Consultation déjà ouverte (compléter/retour arrière) → on enregistre
                        // observations/analyses (PUT) sans clôturer, puis on ferme le modal.
                        if (draftConsultationId) {
                          void saveOpenConsultationObservations().then((ok) => {
                            if (ok) {
                              setShowAddConsultation(false);
                              resetConsultationForm();
                            }
                          });
                          return;
                        }
                        void handleSubmitConsultation(true);
                      }}
                      className="h-10 px-4 rounded-[6px] font-semibold border disabled:opacity-50"
                      style={{ borderColor: '#00553E', color: '#00553E' }}
                    >
                      {savingConsultation ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button
                      type="button"
                      disabled={
                        savingConsultation ||
                        !newConsultation.observations.trim() ||
                        (!draftConsultationId && !selectedEtablissementId)
                      }
                      onClick={() => {
                        // Consultation déjà ouverte (compléter/retour arrière) → on enregistre
                        // observations/analyses (PUT) avant d'avancer à l'étape Résultats & Ordonnances.
                        if (draftConsultationId) {
                          void saveOpenConsultationObservations().then((ok) => {
                            if (ok) setConsultationStep(1);
                          });
                          return;
                        }
                        void handleSubmitConsultation();
                      }}
                      className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-50"
                      style={{ background: '#00553E' }}
                    >
                      {savingConsultation ? 'Ouverture…' : 'Suivant →'}
                    </button>
                  </>
                ) : consultationStep === 1 && draftConsultationStatus === "fermee" ? (
                  <button
                    type="button"
                    onClick={handleModalTerminerApresEnvoi}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white"
                    style={{ background: "#00553E" }}
                  >
                    Terminer
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={savingConsultation || draftConsultationStatus === "fermee"}
                    onClick={handleSubmitConsultation}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                    style={{ background: '#00553E' }}
                  >
                  {savingConsultation
                    ? draftConsultationId
                      ? "Clôture…"
                      : "Ouverture…"
                    : draftConsultationId
                      ? "Clôturer la consultation"
                      : "Ouvrir la consultation"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <PatientAccessOtpModal
        open={needsAccessOtp}
        patientNip={displayPatient.nip || (id ? decodeURIComponent(id) : "")}
        patientDisplayName={[displayPatient.prenom, displayPatient.nom].filter(Boolean).join(" ") || undefined}
        estMineur={accessRequiredPatientEstMineur}
        contactsSureteDisponibles={accessRequiredContacts}
        accesUrgenceDisponible={accessUrgenceDisponible}
        onClose={() => {
          setNeedsAccessOtp(false);
          navigate(-1);
        }}
        onSuccess={() => {
          setNeedsAccessOtp(false);
          void reloadPatientData();
        }}
      />
    </div>
  );
}